import { useRef, useState } from 'react';
import { useClickOutside } from '../../lib/useClickOutside';

// Granular Refresh spec §2.3. Collapsed: a single truncated line of the
// Campaign's current value. Expandable inline ("Show all N →") when the
// value is a multi-element array or a long string. No modal-within-modal —
// the expanded content renders in place. Clicking outside the expanded
// block collapses it (pointerdown via useClickOutside).

const MAX_CHARS = 90;

type Props = {
  // Joined one-line preview (manifest entry.preview).
  text: string;
  // Per-element list for array fields (manifest entry.items). When present
  // with >1 entry the expand affordance shows "Show all {items.length} →".
  items?: string[];
};

export function TruncatedFieldPreview({ text, items }: Props) {
  const [expanded, setExpanded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const isList = !!items && items.length > 1;
  const isLongText = text.length > MAX_CHARS;
  const canExpand = isList || isLongText;

  useClickOutside([ref], () => setExpanded(false), expanded);

  if (!canExpand) {
    return <span className="block text-xs text-slate-500">{text}</span>;
  }

  const toggleLabel = expanded
    ? 'Show less'
    : isList
      ? `Show all ${items!.length} →`
      : 'Show more →';

  return (
    <div ref={ref} className="text-xs text-slate-500">
      {expanded ? (
        isList ? (
          <ul className="list-disc space-y-0.5 pl-4">
            {items!.map((item, i) => (
              <li key={i} className="break-words text-slate-600">
                {item || <span className="italic text-slate-400">(empty)</span>}
              </li>
            ))}
          </ul>
        ) : (
          <span className="block whitespace-pre-wrap break-words text-slate-600">
            {text}
          </span>
        )
      ) : (
        <span className="block truncate">{text}</span>
      )}
      <button
        type="button"
        // Stop propagation so the row's <label> doesn't toggle the
        // checkbox when the user is only expanding the preview.
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setExpanded((v) => !v);
        }}
        className="mt-1 text-xs font-medium text-sky-600 hover:text-sky-800"
      >
        {toggleLabel}
      </button>
    </div>
  );
}
